import * as Sentry from '@sentry/react'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'

export function addSentryInterceptors(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const startTime = Date.now()

      if (config.metadata) {
        config.metadata.startTime = startTime
      } else {
        config.metadata = { startTime }
      }

      Sentry.addBreadcrumb({
        message: `API Request: ${config.method?.toUpperCase()} ${config.url}`,
        category: 'api',
        level: 'info',
        data: {
          method: config.method,
          url: config.url,
          baseURL: config.baseURL
        },
        timestamp: startTime / 1000
      })

      config.metadata.apiCallInfo = {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL
      }

      return config
    },
    (error: AxiosError) => {
      Sentry.captureException(error, {
        tags: {
          component: 'api-interceptor',
          phase: 'request-setup'
        }
      })

      return Promise.reject(error)
    }
  )

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      const config = response.config
      const endTime = Date.now()
      const startTime = config.metadata?.startTime || endTime
      const duration = endTime - startTime

      Sentry.addBreadcrumb({
        message: `API Response: ${response.status} ${config.method?.toUpperCase()} ${config.url}`,
        category: 'api',
        level: 'info',
        data: {
          status: response.status,
          statusText: response.statusText,
          duration: `${duration}ms`,
          method: config.method,
          url: config.url
        },
        timestamp: endTime / 1000
      })

      Sentry.setMeasurement('api.response_time', duration, 'millisecond')
      Sentry.setTag('api.status', response.status.toString())

      return response
    },
    (error: AxiosError) => {
      const config = error.config
      const endTime = Date.now()
      const startTime = config?.metadata?.startTime || endTime
      const duration = endTime - startTime

      // Add error breadcrumb
      Sentry.addBreadcrumb({
        message: `API Error: ${error.response?.status || 'Network'} ${config?.method?.toUpperCase()} ${config?.url}`,
        category: 'api',
        level: 'error',
        data: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          duration: `${duration}ms`,
          method: config?.method,
          url: config?.url,
          errorMessage: error.message
        },
        timestamp: endTime / 1000
      })

      Sentry.setTag('api.error', true)
      Sentry.setTag('api.status', error.response?.status?.toString() || 'network-error')
      Sentry.setMeasurement('api.error_time', duration, 'millisecond')

      if (!error.response || error.response.status >= 500) {
        Sentry.captureException(error, {
          tags: {
            component: 'api-interceptor',
            phase: 'response',
            status: error.response?.status?.toString() || 'network-error',
            method: config?.method,
            url: config?.url
          },
          contexts: {
            api_call: {
              method: config?.method,
              url: config?.url,
              baseURL: config?.baseURL,
              status: error.response?.status,
              statusText: error.response?.statusText,
              duration
            }
          }
        })
      }

      return Promise.reject(error)
    }
  )
}

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime?: number
      apiCallInfo?: {
        method?: string
        url?: string
        baseURL?: string
      }
    }
    _retry?: boolean
  }
}
